import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { fmt } from '../api/services.api';
import { useCartStore } from '../../cart/store/cartStore';
import { useToast } from '../../../shared/components/Toast/ToastProvider';
import { HOME_CLEANING_CONFIG } from '../data/customFlows';

const PRIMARY = '#082189';

/* ─── Modal shell ─── */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100010;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;

  @media (max-width: 480px) {
    padding: 0;
    align-items: flex-end;
  }
`;

const Card = styled.div`
  background: #ffffff;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: 480px) {
    max-height: 90vh;
    max-height: 90dvh;
    border-radius: 20px 20px 0 0;
  }
`;

const HeaderBar = styled.div`
  position: sticky;
  top: 0;
  background: #ffffff;
  border-bottom: 1px solid #eef0f4;
  padding: 18px 20px 14px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;

  @media (max-width: 480px) {
    padding: 14px 16px 12px;
  }
`;

const HeaderText = styled.div`
  min-width: 0;
`;

const Title = styled.h2`
  margin: 0 0 4px;
  font-size: 17px;
  font-weight: 900;
  color: #111827;
  letter-spacing: -0.3px;
  line-height: 1.3;
`;

const Meta = styled.p`
  margin: 0;
  font-size: 12.5px;
  color: #6b7280;
`;

const CloseBtn = styled.button`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: #f3f4f6;
  color: #374151;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: #e5e7eb; }
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 8px 20px 16px;

  @media (max-width: 480px) {
    padding: 6px 14px 14px;
  }
`;

const FooterBar = styled.div`
  position: sticky;
  bottom: 0;
  flex-shrink: 0;
  background: #ffffff;
  border-top: 1px solid #eef0f4;
  padding: 14px 20px;
  padding-bottom: max(14px, env(safe-area-inset-bottom));
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.06);

  @media (max-width: 480px) {
    padding: 12px 16px;
    padding-bottom: max(12px, env(safe-area-inset-bottom));
  }
`;

const TotalCol = styled.div`
  display: flex;
  flex-direction: column;
`;

const TotalLabel = styled.span`
  font-size: 11px;
  color: #9ca3af;
  font-weight: 500;
`;

const TotalValue = styled.span`
  font-size: 20px;
  font-weight: 900;
  color: #111827;
`;

const DoneBtn = styled.button`
  border: none;
  cursor: pointer;
  background: ${PRIMARY};
  color: #ffffff;
  font-size: 14px;
  font-weight: 800;
  padding: 12px 26px;
  border-radius: 10px;
  transition: opacity 0.15s, transform 0.12s;

  &:hover { opacity: 0.92; transform: scale(1.02); }
`;

/* ─── Accordion section ─── */
const Section = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  margin-top: 12px;
  overflow: hidden;
`;

const SectionHead = styled.button`
  width: 100%;
  background: ${p => (p.$open ? '#f9fafb' : '#ffffff')};
  border: none;
  cursor: pointer;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  text-align: left;
`;

const SectionTitleCol = styled.div`
  min-width: 0;
`;

const SectionTitle = styled.div`
  font-size: 13.5px;
  font-weight: 800;
  color: #111827;
`;

const SectionSummary = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
`;

const Chevron = styled.span`
  flex-shrink: 0;
  font-size: 15px;
  color: #9ca3af;
  transform: ${p => (p.$open ? 'rotate(180deg)' : 'rotate(0)')};
  transition: transform 0.2s ease;
`;

const SectionBody = styled.div`
  padding: 6px 16px 14px;
`;

/* ─── Rows ─── */
const OptionRow = styled.button`
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  padding: 11px 4px;
  display: flex;
  align-items: center;
  gap: 11px;
  text-align: left;
  border-bottom: 1px solid #f3f4f6;

  &:last-child { border-bottom: none; }
`;

const Radio = styled.span`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid ${p => (p.$checked ? PRIMARY : '#cbd5e1')};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 3px;
    border-radius: 50%;
    background: ${p => (p.$checked ? PRIMARY : 'transparent')};
  }
`;

const OptionBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const OptionTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const OptionLabel = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #111827;
  line-height: 1.4;
`;

const OptionPriceCol = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const OptionPrice = styled.span`
  font-size: 14px;
  font-weight: 800;
  color: #111827;
`;

const OptionStrike = styled.span`
  font-size: 11.5px;
  color: #9ca3af;
  text-decoration: line-through;
`;

const OptionMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 3px;
`;

const OptionPerUnit = styled.span`
  font-size: 11.5px;
  color: #9ca3af;
`;

const OptionOff = styled.span`
  font-size: 11.5px;
  font-weight: 800;
  color: #16a34a;
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 11px 4px;
  border-bottom: 1px solid #f3f4f6;

  &:last-child { border-bottom: none; }
`;

const ItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemName = styled.div`
  font-size: 13px;
  color: #374151;
  line-height: 1.4;
`;

const ItemPrice = styled.div`
  font-size: 12.5px;
  font-weight: 700;
  color: #111827;
  margin-top: 2px;
`;

const AddBtn = styled.button`
  flex-shrink: 0;
  border: 1.5px solid ${PRIMARY};
  background: #ffffff;
  color: ${PRIMARY};
  font-size: 12.5px;
  font-weight: 800;
  padding: 7px 18px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover { background: ${PRIMARY}0d; }
`;

const Stepper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1.5px solid ${PRIMARY};
  border-radius: 8px;
  padding: 4px 10px;
`;

const StepBtn = styled.button`
  border: none;
  background: none;
  color: ${PRIMARY};
  font-size: 16px;
  font-weight: 800;
  line-height: 1;
  cursor: pointer;
  padding: 0;
`;

const StepQty = styled.span`
  font-size: 13px;
  font-weight: 800;
  color: #111827;
  min-width: 12px;
  text-align: center;
`;

const SelectRequirementsModal = ({ open, onClose, ctx = {}, destination = '/cart', config }) => {
  const navigate = useNavigate();
  const addItem = useCartStore(s => s.addItem);
  const toast = useToast();

  const cfg = config ?? HOME_CLEANING_CONFIG;

  // First section open by default.
  const [openId, setOpenId] = useState(cfg.groups[0]?.id ?? null);

  // Single-select values: default first option of each single group.
  const [singleSel, setSingleSel] = useState(() => {
    const init = {};
    cfg.groups.forEach(g => {
      if (g.type === 'single') init[g.id] = g.options[0]?.id ?? null;
    });
    return init;
  });

  // Multi-select quantities: { [groupId]: { [itemId]: qty } }
  const [multiQty, setMultiQty] = useState({});

  const getQty = (gid, iid) => multiQty[gid]?.[iid] ?? 0;

  const setQty = (gid, iid, qty) => {
    setMultiQty(prev => {
      const grp = { ...(prev[gid] || {}) };
      if (qty <= 0) delete grp[iid];
      else grp[iid] = qty;
      return { ...prev, [gid]: grp };
    });
  };

  const total = useMemo(() => {
    let sum = 0;
    cfg.groups.forEach(g => {
      if (g.type === 'single') {
        const opt = g.options.find(o => o.id === singleSel[g.id]);
        if (opt) sum += opt.price;
      } else if (g.type === 'multi') {
        g.items.forEach(it => { sum += it.price * getQty(g.id, it.id); });
      }
    });
    return sum;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleSel, multiQty]);

  if (!open) return null;

  const summaryFor = (g) => {
    if (g.type === 'single') {
      const opt = g.options.find(o => o.id === singleSel[g.id]);
      return opt ? `${opt.label} · ${fmt(opt.price)}` : 'Select an option';
    }
    const count = g.items.reduce((n, it) => n + (getQty(g.id, it.id) > 0 ? 1 : 0), 0);
    return count > 0 ? `${count} selected` : 'Optional add-ons';
  };

  const handleDone = () => {
    cfg.groups.forEach(g => {
      if (g.type === 'single') {
        const opt = g.options.find(o => o.id === singleSel[g.id]);
        if (opt) {
          addItem({
            subServiceID: `cfg-${g.id}-${opt.id}`,
            subServiceName: `${g.title.replace(/^Select /i, '')}: ${opt.label}`,
            quantity: 1,
            price: opt.price,
            mrp: opt.mrp ?? opt.price,
            cgst: 0,
            sgst: 0,
            imageURL: '',
            serviceID: ctx.serviceID,
            subCategoryID: ctx.subCategoryID,
            categoryName: ctx.categoryName,
            categorySlug: ctx.categorySlug,
            packageID: 1,
          });
        }
      } else if (g.type === 'multi') {
        g.items.forEach(it => {
          const qty = getQty(g.id, it.id);
          if (qty > 0) {
            addItem({
              subServiceID: `cfg-${g.id}-${it.id}`,
              subServiceName: it.name,
              quantity: qty,
              price: it.price,
              cgst: 0,
              sgst: 0,
              imageURL: '',
              serviceID: ctx.serviceID,
              subCategoryID: ctx.subCategoryID,
              categoryName: ctx.categoryName,
              categorySlug: ctx.categorySlug,
              packageID: 1,
            });
          }
        });
      }
    });
    toast.success('Added to cart');
    onClose?.();
    navigate(destination);
  };

  return (
    <Overlay onClick={onClose}>
      <Card onClick={e => e.stopPropagation()}>
        <HeaderBar>
          <HeaderText>
            <Title>{cfg.title}</Title>
            <Meta>{cfg.meta}</Meta>
          </HeaderText>
          <CloseBtn onClick={onClose} aria-label="Close">×</CloseBtn>
        </HeaderBar>

        <Body>
          {cfg.groups.map(g => {
            const isOpen = openId === g.id;
            return (
              <Section key={g.id}>
                <SectionHead
                  $open={isOpen}
                  onClick={() => setOpenId(isOpen ? null : g.id)}
                >
                  <SectionTitleCol>
                    <SectionTitle>{g.title}</SectionTitle>
                    <SectionSummary>{summaryFor(g)}</SectionSummary>
                  </SectionTitleCol>
                  <Chevron $open={isOpen}>▾</Chevron>
                </SectionHead>

                {isOpen ? (
                  <SectionBody>
                    {g.type === 'single'
                      ? g.options.map(o => {
                          const off = o.mrp && o.mrp > o.price
                            ? Math.round(((o.mrp - o.price) / o.mrp) * 100)
                            : 0;
                          const perUnit = o.count ? Math.round(o.price / o.count) : null;
                          return (
                            <OptionRow
                              key={o.id}
                              onClick={() => setSingleSel(s => ({ ...s, [g.id]: o.id }))}
                            >
                              <Radio $checked={singleSel[g.id] === o.id} />
                              <OptionBody>
                                <OptionTopRow>
                                  <OptionLabel>{o.label}</OptionLabel>
                                  <OptionPriceCol>
                                    <OptionPrice>{fmt(o.price)}</OptionPrice>
                                    {off > 0 ? <OptionStrike>{fmt(o.mrp)}</OptionStrike> : null}
                                  </OptionPriceCol>
                                </OptionTopRow>
                                {(perUnit != null || off > 0) ? (
                                  <OptionMeta>
                                    {perUnit != null ? (
                                      <OptionPerUnit>{fmt(perUnit)}/{g.unit || 'unit'}</OptionPerUnit>
                                    ) : null}
                                    {off > 0 ? <OptionOff>{off}% Off</OptionOff> : null}
                                  </OptionMeta>
                                ) : null}
                              </OptionBody>
                            </OptionRow>
                          );
                        })
                      : g.items.map(it => {
                          const qty = getQty(g.id, it.id);
                          return (
                            <ItemRow key={it.id}>
                              <ItemInfo>
                                <ItemName>{it.name}</ItemName>
                                <ItemPrice>{fmt(it.price)}</ItemPrice>
                              </ItemInfo>
                              {qty > 0 ? (
                                <Stepper>
                                  <StepBtn onClick={() => setQty(g.id, it.id, qty - 1)}>−</StepBtn>
                                  <StepQty>{qty}</StepQty>
                                  <StepBtn onClick={() => setQty(g.id, it.id, qty + 1)}>+</StepBtn>
                                </Stepper>
                              ) : (
                                <AddBtn onClick={() => setQty(g.id, it.id, 1)}>Add</AddBtn>
                              )}
                            </ItemRow>
                          );
                        })}
                  </SectionBody>
                ) : null}
              </Section>
            );
          })}
        </Body>

        <FooterBar>
          <TotalCol>
            <TotalLabel>Total</TotalLabel>
            <TotalValue>{fmt(total)}</TotalValue>
          </TotalCol>
          <DoneBtn onClick={handleDone}>Done →</DoneBtn>
        </FooterBar>
      </Card>
    </Overlay>
  );
};

export default SelectRequirementsModal;
